import { ConfirmationDialog } from '@/components/ConfirmationDialog';
import { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

interface DeleteQueryModalProps {
  isOpen: boolean;
  onClose: () => void;
  query: SavedQuery | null;
  onConfirm: () => void;
  isDeleting: boolean;
}

/**
 * DeleteQueryModal - Confirmation dialog before deleting a saved query
 *
 * Shows query name and description to confirm deletion.
 * Prevents accidental deletions of custom queries.
 *
 * Props:
 * - isOpen: Modal visibility state
 * - onClose: Handler to close modal without deleting
 * - query: The query to be deleted (or null if no selection)
 * - onConfirm: Handler called when user confirms deletion
 * - isDeleting: Loading state during mutation
 */
export const DeleteQueryModal = ({
  isOpen,
  onClose,
  query,
  onConfirm,
  isDeleting,
}: DeleteQueryModalProps) => {
  if (!query) return null;

  const descriptionParts = [
    `Are you sure you want to delete "${query.name}"?`,
    query.description ? query.description : null,
    'This action cannot be undone.',
  ].filter(Boolean);

  return (
    <ConfirmationDialog
      open={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Delete Query"
      description={descriptionParts.join(' ')}
      confirmLabel="Delete Query"
      loadingLabel="Deleting..."
      variant="destructive"
      isLoading={isDeleting}
      data-testid="delete-query-modal"
    />
  );
};
